import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Share2, Download, X, Loader2, Copy, Twitter, Linkedin, Facebook, Instagram } from "lucide-react";

/**
 * Renders a "Share this win" modal. Fetches a branded PNG from POST /api/share/win
 * and offers Download, Web Share (mobile/supported browsers), Copy image, and
 * quick Twitter/LinkedIn intents.
 */
export function ShareWinModal({ payload, onClose }) {
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    let url;
    (async () => {
      setLoading(true);
      try {
        const res = await api.post("/share/win", payload, { responseType: "blob" });
        if (!alive) return;
        setBlob(res.data);
        url = URL.createObjectURL(res.data);
        setPreviewUrl(url);
      } catch { toast.error("Couldn't generate your share card. Try again."); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, []);

  const filename = `bcome-${(payload.kind || "win").toLowerCase()}.png`;

  const download = () => {
    if (!previewUrl) return;
    const a = document.createElement("a");
    a.href = previewUrl; a.download = filename; a.click();
    toast.success("Downloaded.");
  };

  const webShare = async () => {
    if (!blob || !navigator.canShare) { download(); return; }
    try {
      const file = new File([blob], filename, { type: "image/png" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: payload.title,
          text: shareText(payload),
        });
        return;
      }
      download();
    } catch { /* user cancelled */ }
  };

  const copyImage = async () => {
    if (!blob) return;
    try {
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      toast.success("Image copied. Paste into your post.");
    } catch { toast.error("Copy not supported in this browser. Use Download instead."); }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(shareText(payload));
      toast.success("Caption copied.");
    } catch { /* ignore */ }
  };

  const twitter = () => {
    const url = "https://twitter.com/intent/tweet?text=" + encodeURIComponent(shareText(payload));
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const linkedin = () => {
    const url = "https://www.linkedin.com/feed/?shareActive=true&text=" + encodeURIComponent(shareText(payload));
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const facebook = () => {
    // Facebook's sharer.php requires a URL. Include our brand URL; user pastes the image manually.
    const brandUrl = "https://b-come.ai";
    const url = "https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(brandUrl)
      + "&quote=" + encodeURIComponent(shareText(payload));
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const instagram = async () => {
    // No web-compose intent exists for Instagram. Prep the user: save image + copy caption.
    try { await navigator.clipboard.writeText(shareText(payload)); } catch { /* ignore */ }
    download();
    toast.success("Image saved & caption copied — open Instagram to post.");
  };

  return createPortal(
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[85] flex items-center justify-center bg-[#2D3748]/60 p-4 backdrop-blur-sm" onClick={onClose}>
        <motion.div initial={{ scale: 0.9, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", stiffness: 280, damping: 24 }}
          className="relative max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-[2rem] bg-white p-6 shadow-warm-lg sm:p-8"
          onClick={(e) => e.stopPropagation()} data-testid="share-modal">
          <button onClick={onClose} data-testid="share-close-btn"
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-[#FFF9F5] text-[#718096] hover:bg-[#FFE8E3]"><X className="h-4 w-4" /></button>

          <h3 className="flex items-center gap-2 font-nunito text-2xl font-extrabold text-[#2D3748]">
            <Share2 className="h-6 w-6 text-[#FF7E67]" strokeWidth={2.5} /> Share this win
          </h3>
          <p className="mt-1 font-quicksand text-sm text-[#718096]">A little celebration — inspire someone to start theirs.</p>

          <div className="mt-5 overflow-hidden rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5]">
            {loading ? (
              <div className="flex aspect-[1200/630] items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-[#FF7E67]" />
              </div>
            ) : previewUrl ? (
              <img src={previewUrl} alt="Your win" className="w-full" data-testid="share-preview" />
            ) : (
              <div className="flex aspect-[1200/630] items-center justify-center text-[#718096]">Card unavailable</div>
            )}
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <button onClick={webShare} disabled={loading} data-testid="share-web-btn"
              className="flex items-center justify-center gap-2 rounded-full bg-sunrise px-4 py-3 font-nunito text-sm font-bold text-white shadow-glow-coral transition-transform hover:scale-[1.02] disabled:opacity-60 sm:col-span-2">
              <Share2 className="h-4 w-4" strokeWidth={2.5} /> Share
            </button>
            <button onClick={download} disabled={loading} data-testid="share-download-btn"
              className="flex items-center justify-center gap-2 rounded-full border border-[#FFE8E3] px-4 py-3 font-nunito text-sm font-bold text-[#C2410C] hover:bg-[#FFF9F5]">
              <Download className="h-4 w-4" strokeWidth={2.5} /> Download
            </button>
            <button onClick={copyImage} disabled={loading} data-testid="share-copy-btn"
              className="flex items-center justify-center gap-2 rounded-full border border-[#FFE8E3] px-4 py-3 font-nunito text-sm font-bold text-[#C2410C] hover:bg-[#FFF9F5]">
              <Copy className="h-4 w-4" strokeWidth={2.5} /> Copy image
            </button>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="font-quicksand text-xs text-[#718096]">Quick post:</span>
            <button onClick={facebook} data-testid="share-facebook-btn"
              className="flex items-center gap-1.5 rounded-full border border-[#FFE8E3] px-3 py-1.5 font-nunito text-xs font-bold text-[#2D3748] hover:bg-[#FFF9F5]">
              <Facebook className="h-3.5 w-3.5" /> Facebook
            </button>
            <button onClick={instagram} disabled={loading} data-testid="share-instagram-btn"
              className="flex items-center gap-1.5 rounded-full border border-[#FFE8E3] px-3 py-1.5 font-nunito text-xs font-bold text-[#2D3748] hover:bg-[#FFF9F5] disabled:opacity-60">
              <Instagram className="h-3.5 w-3.5" /> Instagram
            </button>
            <button onClick={linkedin} data-testid="share-linkedin-btn"
              className="flex items-center gap-1.5 rounded-full border border-[#FFE8E3] px-3 py-1.5 font-nunito text-xs font-bold text-[#2D3748] hover:bg-[#FFF9F5]">
              <Linkedin className="h-3.5 w-3.5" /> LinkedIn
            </button>
            <button onClick={twitter} data-testid="share-twitter-btn"
              className="flex items-center gap-1.5 rounded-full border border-[#FFE8E3] px-3 py-1.5 font-nunito text-xs font-bold text-[#2D3748] hover:bg-[#FFF9F5]">
              <Twitter className="h-3.5 w-3.5" /> X
            </button>
            <button onClick={copyCaption} data-testid="share-copy-caption-btn"
              className="rounded-full border border-[#FFE8E3] px-3 py-1.5 font-nunito text-xs font-bold text-[#718096] hover:bg-[#FFF9F5]">
              Copy caption
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

function shareText({ kind, title, subtitle }) {
  const line = subtitle ? `${title} — ${subtitle}` : title;
  const emoji = { badge: "🎉", streak: "🔥", goal: "🏁", week: "📈", milestone: "✨" }[kind] || "✨";
  return `${emoji} ${line}\n\nBuilding my future with b→come — the AI Life OS.`;
}

/** Small pill-shaped button — reusable trigger. */
export function ShareWinButton({ payload, label = "Share this win", testid = "share-win-btn", className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={testid}
        className={"inline-flex items-center gap-2 rounded-full bg-[#FFE8E3] px-4 py-2 font-nunito text-xs font-bold text-[#C2410C] transition-colors hover:bg-[#FFD4C7] " + className}>
        <Share2 className="h-3.5 w-3.5" strokeWidth={2.5} /> {label}
      </button>
      {open && <ShareWinModal payload={payload} onClose={() => setOpen(false)} />}
    </>
  );
}
